/**
 * FW-STABILITY-SOAK-1 — Card de ESTABILIDADE do dispositivo.
 *
 * Consome GET /api/admin/devices/{id}/stability-history (agrega heartbeats_raw,
 * read-only): tendência de heap, uptime, reboots (com motivo), Wi-Fi disconnects
 * e um VEREDITO de risco (ok/warn/risk) com detector de leak por inclinação de
 * heap. Útil p/ acompanhar soak test de 24/48/72h sem flash. Espelha o padrão do
 * EnergyHistoryPanel (lazy fetch + sparkline SVG + stat cards).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { getStabilityHistory } from "../lib/api";

const RISK = {
  ok:      { label: "ESTÁVEL",   cls: "text-noc-ok border-noc-ok/40 bg-noc-ok/10" },
  warn:    { label: "ATENÇÃO",   cls: "text-noc-warn border-noc-warn/40 bg-noc-warn/10" },
  risk:    { label: "RISCO",     cls: "text-noc-err border-noc-err/40 bg-noc-err/10" },
  unknown: { label: "SEM DADOS", cls: "text-noc-muted border-noc-border bg-noc-panel2" },
};

function StatCard({ label, value, sub, tone }) {
  const toneCls = tone === "err" ? "text-noc-err"
    : tone === "warn" ? "text-noc-warn"
    : tone === "ok" ? "text-noc-ok" : "text-noc-text";
  return (
    <div className="rounded-lg bg-noc-bg/40 border border-noc-border/40 px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.12em] text-noc-faint">{label}</div>
      <div className={`text-sm font-mono font-semibold ${toneCls}`}>{value}</div>
      {sub && <div className="text-[9px] text-noc-muted mt-0.5">{sub}</div>}
    </div>
  );
}

/** sparkline de heap_free_mean (tendência relativa ao próprio range). */
function HeapSparkline({ buckets }) {
  const pts = useMemo(
    () => buckets.map((b) => b.heap_free_mean).filter((v) => v != null),
    [buckets],
  );
  if (pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts);
  const span = Math.max(1, max - min);
  const W = 280, H = 44;
  const d = pts
    .map((v, i) => {
      const x = (i / (pts.length - 1)) * W;
      const y = H - 2 - ((v - min) / span) * (H - 4);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-11" preserveAspectRatio="none">
      <path d={d} fill="none" stroke="#2BD9F0" strokeWidth="1.5" />
    </svg>
  );
}

function fmtB(n) {
  if (n == null) return "—";
  return (n / 1024).toFixed(0) + " KB";
}

export default function StabilityHistoryPanel({ deviceId, hours = 72 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const acRef = useRef(null);

  useEffect(() => {
    if (!deviceId) return;
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;
    setLoading(true); setErr(null);
    getStabilityHistory(deviceId, { hours, bucketMinutes: 30, signal: ac.signal })
      .then((r) => setData(r))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha"); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [deviceId, hours]);

  const s = data?.summary || {};
  const risk = RISK[data?.risk] || RISK.unknown;
  const slope = s.heap_slope_bytes_per_hour;
  const slopeTone = slope == null ? undefined
    : slope < -2000 ? "err" : slope < -500 ? "warn" : "ok";
  const floorTone = s.heap_min_floor == null ? undefined
    : s.heap_min_floor < 25000 ? "err" : s.heap_min_floor < 50000 ? "warn" : "ok";

  return (
    <div className="glass-card-pad space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
          Estabilidade ({hours}h)
        </div>
        {data && (
          <span className={`font-mono text-[10px] tracking-[0.12em] rounded-md border px-2 py-0.5 ${risk.cls}`}>
            {risk.label}
          </span>
        )}
      </div>

      {loading && <div className="text-xs text-noc-muted py-2">carregando…</div>}
      {err && <div className="text-xs text-noc-err py-2">{err}</div>}

      {data && !loading && (
        <>
          {(data.risk_reasons || []).length > 0 && (
            <div className="text-[11px] text-noc-warn">
              {data.risk_reasons.join(" · ")}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <StatCard label="Heap (chão)" value={fmtB(s.heap_min_floor)} tone={floorTone}
                      sub={`livre mín ${fmtB(s.heap_free_min)}`} />
            <StatCard label="Tendência heap"
                      value={slope == null ? "—" : `${slope > 0 ? "+" : ""}${Math.round(slope)} B/h`}
                      tone={slopeTone}
                      sub={s.longest_session_h ? `sessão ${s.longest_session_h}h` : "—"} />
            <StatCard label="Uptime máx" value={s.max_uptime_h != null ? `${s.max_uptime_h} h` : "—"} />
            <StatCard label="Reboots" value={s.reboot_count ?? "—"}
                      tone={(s.anomalous_reboot_count || 0) > 0 ? "err" : undefined}
                      sub={`${s.anomalous_reboot_count || 0} anômalo(s)`} />
            <StatCard label="Wi-Fi quedas" value={s.wifi_disconnects_max ?? "—"} />
            <StatCard label="Amostras" value={data.samples}
                      sub={data.partial ? `parcial ${data.coverage_hours}h` : `${data.coverage_hours}h`} />
          </div>

          {data.buckets?.length > 1 && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.12em] text-noc-faint mb-1">
                heap livre (tendência)
              </div>
              <HeapSparkline buckets={data.buckets} />
            </div>
          )}

          {(s.reset_reason_counts && Object.keys(s.reset_reason_counts).length > 0) && (
            <div className="text-[10px] text-noc-muted">
              motivos de reboot:{" "}
              {Object.entries(s.reset_reason_counts)
                .map(([r, n]) => `${r}×${n}`).join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
